/**
 * HVP DOM Patcher - option label formatting
 *
 * Cached slug -> display text resolution: WooCommerce term labels first,
 * then provided label, then attribute-specific formatting, then Title Case.
 *
 * Split out of dom-patcher.js (350-line rule): the methods below moved
 * VERBATIM and are installed on HVPDomPatcher.prototype by
 * dom-patcher.js, so `this` is the same instance and behavior is identical.
 *
 * @package HaruVariationsPro
 * @version 1.0.0
 */

export const DomPatcherLabelMethods = {
    /**
     * Format option display text with caching
     *
     * Priority order:
     * 1. WooCommerce term labels (proper display names from wp_terms)
     * 2. Provided label (if different from value)
     * 3. Special formatting (bindery, turnaround)
     * 4. Fallback: slug-to-Title-Case
     *
     * @param {string} value - The option value (slug)
     * @param {string} label - The provided label (if any)
     * @param {string} attrName - Attribute name for context
     * @returns {string} Formatted display text
     */
    formatDisplayText(value, label, attrName = '') {
        // Check cache first for any prior result
        const cacheKey = `${attrName}|${value}`;
        if (this.labelCache.has(cacheKey)) {
            this.stats.cacheHits++;
            return this.labelCache.get(cacheKey);
        }

        this.stats.cacheMisses++;

        let formatted = null;

        // PRIORITY 1: WooCommerce term labels (proper display names)
        // Normalize attribute name (handle both "pa_colors" and "attribute_pa_colors")
        const normalizedAttr = attrName.replace('attribute_', '');
        if (this.termLabels[normalizedAttr] && this.termLabels[normalizedAttr][value]) {
            formatted = this.termLabels[normalizedAttr][value];
            this.stats.termLabelHits++;
            this.log(`Term label found for ${normalizedAttr}/${value}:`, formatted);
        }

        // PRIORITY 2: Provided label (if different from value)
        if (!formatted && label && label !== value) {
            formatted = label;
        }

        // PRIORITY 3 & 4: Special formatting or slug-to-Title-Case
        if (!formatted) {
            // Special handling for specific attribute types
            if (attrName.includes('bindery') || attrName.includes('rounded')) {
                formatted = this.formatBinderyAttribute(value);
            } else if (attrName.includes('turnaround') || attrName.includes('time')) {
                formatted = this.formatTurnaroundAttribute(value);
            } else {
                // Default: slug-to-Title-Case
                formatted = this.slugToTitleCase(value);
            }
        }

        // Enforce cache size limit (LRU eviction)
        if (this.labelCache.size >= this.maxCacheSize) {
            // Remove oldest entry (first key in Map)
            const oldestKey = this.labelCache.keys().next().value;
            if (oldestKey) {
                this.labelCache.delete(oldestKey);
            }
        }

        // Cache the result
        this.labelCache.set(cacheKey, formatted);
        return formatted;
    },

    /**
     * Convert slug to Title Case
     *
     * @param {string} slug - The slug to convert
     * @returns {string} Title cased text
     */
    slugToTitleCase(slug) {
        return slug
            .split('-')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
    },

    /**
     * Format bindery attribute values
     *
     * Handles special formatting for bindery-related attributes
     * Example: "rounded-all-4-1-8" -> "Rounded all 4 (1/8")"
     *
     * @param {string} value - The value to format
     * @returns {string} Formatted text
     */
    formatBinderyAttribute(value) {
        // Pattern: rounded-all-4-1-8 -> Rounded all 4 (1/8")
        const fractionPattern = /^(.+)-(\d+)-(\d+)$/;
        const match = value.match(fractionPattern);

        if (match) {
            const basePart = this.slugToTitleCase(match[1]);
            const numerator = match[2];
            const denominator = match[3];
            return `${basePart} (${numerator}/${denominator}")`;
        }

        // Default formatting
        return this.slugToTitleCase(value);
    },

    /**
     * Format turnaround attribute values
     *
     * Handles special formatting for turnaround time attributes
     * Example: "3-business-days" -> "3 Business Days"
     *
     * @param {string} value - The value to format
     * @returns {string} Formatted text
     */
    formatTurnaroundAttribute(value) {
        // Already uses standard slug format, just title case it
        return this.slugToTitleCase(value);
    },
};

export default DomPatcherLabelMethods;
